import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { SENDE_OBEN, SENDE_UNTEN, histogramm, parade, sendeSatz, vektorskop } from '@schnitt/shared';
import { Sheet, amSchreibtisch } from './Sheet';
import { useEditor } from '../store';

/**
 * Bildmessgeräte (S15).
 *
 * Sascha: „Histogramm, RGB-Parade, Vektorskop — damit man sieht, ob ein Bild
 * sendefähig ist, statt es zu schätzen.“
 *
 * Gemessen wird das Bild, das gerade auf der Bühne steht -- derselbe Frame,
 * aus dem auch die Pipette ihre Farbe nimmt. Gerechnet wird im gemeinsamen
 * Teil (`messgeraete.ts`), gezeichnet hier. Zwei getrennte Sachen: was eine
 * Kurve *sagt*, lässt sich prüfen; wie sie *aussieht*, nicht.
 */

/** Auf so viele Punkte Breite wird vor dem Messen heruntergerechnet. */
const MESS_BREITE = 240;

/** Die drei Geräte, in der Reihenfolge, in der man sie liest. */
const GERAETE = [
  { id: 'histogramm', name: 'Histogramm' },
  { id: 'parade', name: 'RGB-Parade' },
  { id: 'vektorskop', name: 'Vektorskop' },
] as const;
type Geraet = (typeof GERAETE)[number]['id'];

/** Welches zuletzt offen war -- am Gerät und nicht am Schnitt, wie das Raster. */
const WAHL_SCHLUESSEL = 'schnitt.messgeraet';
function gemerkteWahl(): Geraet {
  try {
    const w = localStorage.getItem(WAHL_SCHLUESSEL);
    if (GERAETE.some((g) => g.id === w)) return w as Geraet;
  } catch {
    /* ohne Speicher eben das erste */
  }
  return 'histogramm';
}

interface Messung {
  hist: ReturnType<typeof histogramm>;
  par: ReturnType<typeof parade>;
  vek: ReturnType<typeof vektorskop>;
  breite: number;
  hoehe: number;
}

/**
 * Den Frame von der Bühne holen.
 *
 * Zwei Wege, je nachdem, wer gerade malt: die Leinwand auf der Grafikkarte
 * (7.69) oder die Videoelemente. Beide liegen unter `.canvas`; genommen wird,
 * was ein Bild hat.
 */
function frameHolen(): ImageData | null {
  if (typeof document === 'undefined') return null;
  // Drei Wege, in dieser Reihenfolge -- je nachdem, wer gerade malt und was
  // dieses Gerät überhaupt dekodieren kann:
  //   1. die Leinwand auf der Grafikkarte (7.69), wenn sie an ist,
  //   2. ein Videoelement, das ein Bild hat,
  //   3. ein Standbild auf der Bühne -- dasselbe, was der Betrachter sieht,
  //      solange das Video (noch) nicht dekodiert.
  // Ohne den dritten Weg misst das Blatt auf einem Gerät ohne H.264-Dekoder
  // gar nichts und sagt „kein Bild“, obwohl eines dasteht.
  const gl = document.querySelector<HTMLCanvasElement>('.canvas canvas');
  const video = [...document.querySelectorAll<HTMLVideoElement>('.canvas video')].find(
    (v) => v.readyState >= 2 && v.videoWidth > 0 && getComputedStyle(v).opacity !== '0',
  );
  const bild = [...document.querySelectorAll<HTMLImageElement>('.canvas img')].find(
    (i) => i.naturalWidth > 0 && getComputedStyle(i).opacity !== '0',
  );
  const quelle: HTMLCanvasElement | HTMLVideoElement | HTMLImageElement | null =
    gl && gl.width > 0 ? gl : (video ?? bild ?? null);
  if (!quelle) return null;
  const qb =
    quelle instanceof HTMLVideoElement ? quelle.videoWidth : quelle instanceof HTMLImageElement ? quelle.naturalWidth : quelle.width;
  const qh =
    quelle instanceof HTMLVideoElement ? quelle.videoHeight : quelle instanceof HTMLImageElement ? quelle.naturalHeight : quelle.height;
  if (!qb || !qh) return null;
  const breite = Math.min(MESS_BREITE, qb);
  const hoehe = Math.max(1, Math.round((breite * qh) / qb));
  try {
    const leinwand = document.createElement('canvas');
    leinwand.width = breite;
    leinwand.height = hoehe;
    const stift = leinwand.getContext('2d', { willReadFrequently: true });
    if (!stift) return null;
    stift.drawImage(quelle, 0, 0, breite, hoehe);
    return stift.getImageData(0, 0, breite, hoehe);
  } catch {
    // Ein Bild aus fremder Quelle macht die Leinwand unlesbar. Dann eben nicht
    // -- das Blatt sagt es, statt eine leere Kurve zu zeigen.
    return null;
  }
}

/** Eine Kurve über die Breite, von unten gefüllt. */
function malKurve(stift: CanvasRenderingContext2D, werte: number[], farbe: string, breite: number, hoehe: number, groesste: number): void {
  if (groesste <= 0) return;
  stift.beginPath();
  stift.moveTo(0, hoehe);
  werte.forEach((n, i) => {
    const x = (i / (werte.length - 1 || 1)) * breite;
    // Wurzel statt gerade: eine einzelne Spitze drückt sonst alles andere
    // platt, und genau das Kleine will man sehen.
    const y = hoehe - Math.sqrt(n / groesste) * hoehe;
    stift.lineTo(x, y);
  });
  stift.lineTo(breite, hoehe);
  stift.closePath();
  stift.fillStyle = farbe;
  stift.fill();
}

export function MessgeraeteSheet({ onClose }: { onClose: () => void }): ReactElement {
  const playhead = useEditor((s) => s.playhead);
  const [messung, setMessung] = useState<Messung | null>(null);
  const [ohneBild, setOhneBild] = useState(false);
  // Am grossen Schirm ist Platz für alle drei; auf dem Telefon eines (7.107).
  const [alleDrei] = useState(amSchreibtisch);
  const [wahl, setWahlRoh] = useState<Geraet>(gemerkteWahl);
  const setWahl = (g: Geraet) => {
    setWahlRoh(g);
    try {
      localStorage.setItem(WAHL_SCHLUESSEL, g);
    } catch {
      /* ohne Speicher gilt sie nur für dieses Blatt */
    }
  };
  const histRef = useRef<HTMLCanvasElement | null>(null);
  const parRef = useRef<HTMLCanvasElement | null>(null);
  const vekRef = useRef<HTMLCanvasElement | null>(null);

  const messen = useCallback(() => {
    const bild = frameHolen();
    if (!bild) {
      setOhneBild(true);
      setMessung(null);
      return;
    }
    setOhneBild(false);
    setMessung({
      hist: histogramm(bild.data, 64),
      par: parade(bild.data, bild.width, bild.height, 48, 48),
      vek: vektorskop(bild.data, 48),
      breite: bild.width,
      hoehe: bild.height,
    });
  }, []);

  // Beim Öffnen einmal, und danach, wenn der Kopf woanders steht: die
  // Messung gehört zu *dieser* Stelle im Film und nicht zu irgendeiner.
  useEffect(() => {
    const t = window.setTimeout(messen, 120);
    return () => window.clearTimeout(t);
  }, [messen, playhead]);

  useEffect(() => {
    if (!messung) return;
    const { hist, par, vek } = messung;
    // Histogramm: drei Kanäle übereinander, additiv gemischt.
    const h = histRef.current;
    if (h) {
      const stift = h.getContext('2d');
      if (stift) {
        stift.clearRect(0, 0, h.width, h.height);
        stift.fillStyle = '#000';
        stift.fillRect(0, 0, h.width, h.height);
        // Die Grenzen des Sendebereichs als senkrechte Linien.
        stift.strokeStyle = 'rgba(255,255,255,0.28)';
        stift.lineWidth = 1;
        for (const grenze of [SENDE_UNTEN, SENDE_OBEN]) {
          const x = Math.round((grenze / 255) * h.width) + 0.5;
          stift.beginPath();
          stift.moveTo(x, 0);
          stift.lineTo(x, h.height);
          stift.stroke();
        }
        const groesste = Math.max(...hist.r, ...hist.g, ...hist.b);
        stift.globalCompositeOperation = 'lighter';
        malKurve(stift, hist.r, 'rgba(244,63,94,0.75)', h.width, h.height, groesste);
        malKurve(stift, hist.g, 'rgba(34,197,94,0.75)', h.width, h.height, groesste);
        malKurve(stift, hist.b, 'rgba(59,130,246,0.75)', h.width, h.height, groesste);
        stift.globalCompositeOperation = 'source-over';
      }
    }
    // Parade: drei Felder nebeneinander, in jedem die Spalten des Bildes.
    const p = parRef.current;
    if (p) {
      const stift = p.getContext('2d');
      if (stift) {
        stift.clearRect(0, 0, p.width, p.height);
        stift.fillStyle = '#000';
        stift.fillRect(0, 0, p.width, p.height);
        const feld = p.width / 3;
        const kanaele: [number[], [number, number, number]][] = [
          [par.r, [244, 63, 94]],
          [par.g, [34, 197, 94]],
          [par.b, [59, 130, 246]],
        ];
        const bildDaten = stift.createImageData(p.width, p.height);
        kanaele.forEach(([werte, [cr, cg, cb]], k) => {
          for (let x = 0; x < feld; x++) {
            const spalte = Math.min(par.spalten - 1, Math.floor((x / feld) * par.spalten));
            for (let y = 0; y < p.height; y++) {
              const stufe = par.stufen - 1 - Math.min(par.stufen - 1, Math.floor((y / p.height) * par.stufen));
              const n = werte[spalte * par.stufen + stufe] ?? 0;
              if (n === 0) continue;
              const a = Math.min(1, Math.sqrt(n / par.groesste));
              const i = (y * p.width + Math.floor(k * feld + x)) * 4;
              bildDaten.data[i] = cr;
              bildDaten.data[i + 1] = cg;
              bildDaten.data[i + 2] = cb;
              bildDaten.data[i + 3] = Math.round(a * 255);
            }
          }
        });
        stift.putImageData(bildDaten, 0, 0);
      }
    }
    // Vektorskop: das Gitter als Punktwolke, mit Kreis und Kreuz darüber.
    const v = vekRef.current;
    if (v) {
      const stift = v.getContext('2d');
      if (stift) {
        stift.clearRect(0, 0, v.width, v.height);
        stift.fillStyle = '#000';
        stift.fillRect(0, 0, v.width, v.height);
        const z = v.width / vek.gitter;
        for (let y = 0; y < vek.gitter; y++) {
          for (let x = 0; x < vek.gitter; x++) {
            const n = vek.werte[y * vek.gitter + x] ?? 0;
            if (n === 0) continue;
            stift.fillStyle = `rgba(226,232,240,${Math.min(1, Math.sqrt(n / vek.groesste)).toFixed(3)})`;
            stift.fillRect(x * z, y * z, Math.ceil(z), Math.ceil(z));
          }
        }
        stift.strokeStyle = 'rgba(255,255,255,0.25)';
        stift.beginPath();
        stift.arc(v.width / 2, v.height / 2, v.width / 2 - 1, 0, Math.PI * 2);
        stift.moveTo(v.width / 2, 0);
        stift.lineTo(v.width / 2, v.height);
        stift.moveTo(0, v.height / 2);
        stift.lineTo(v.width, v.height / 2);
        stift.stroke();
      }
    }
  }, [messung, wahl, alleDrei]);

  return (
    <Sheet title="Messgeräte" onClose={onClose}>
      <div className={`messgeraete ${alleDrei ? 'alle' : 'eines'}`} data-messgeraete data-mess-wahl={wahl}>
        {ohneBild ? (
          <p className="hint" data-messgeraete-ohne>
            Auf der Bühne steht gerade kein Bild, an dem sich etwas messen liesse.
          </p>
        ) : null}
        {/* Auf dem Telefon eines zur Zeit (7.107). Sascha, 17. September:
            „Messgeräte kann man mobil bestimmt auch besser anzeigen.“ Drei
            Kurven untereinander sind über 650px -- man scrollt und sieht nie
            zwei nebeneinander. Am grossen Schirm ist Platz, dort bleiben alle
            drei. */}
        {alleDrei ? null : (
          <div className="chips" data-mess-chips>
            {GERAETE.map((g) => (
              <button
                key={g.id}
                className={`chip ${wahl === g.id ? 'active' : ''}`}
                data-mess-chip={g.id}
                onClick={() => setWahl(g.id)}
              >
                {g.name}
              </button>
            ))}
          </div>
        )}
        {alleDrei || wahl === 'histogramm' ? (
          <>
            <div className="messfeld">
              <span className="messname">Histogramm</span>
              <canvas ref={histRef} width={512} height={alleDrei ? 160 : 280} data-mess-histogramm />
            </div>
            <p className="hint" data-sendesatz style={{ marginTop: 0 }}>
              {messung ? sendeSatz(messung.hist) : '—'}
            </p>
          </>
        ) : null}
        {alleDrei || wahl === 'parade' ? (
          <div className="messfeld">
            <span className="messname">RGB-Parade</span>
            <canvas ref={parRef} width={512} height={alleDrei ? 160 : 280} data-mess-parade />
          </div>
        ) : null}
        {alleDrei || wahl === 'vektorskop' ? (
          <div className="messfeld">
            <span className="messname">Vektorskop</span>
            <canvas ref={vekRef} width={288} height={288} data-mess-vektorskop />
          </div>
        ) : null}
        <button className="row" data-mess-neu onClick={messen}>
          <strong style={{ display: 'block' }}>Neu messen</strong>
          <span className="hint" style={{ margin: 0 }}>
            {messung ? `Gemessen an ${messung.breite}×${messung.hoehe} Punkten der Vorschau.` : 'Noch nichts gemessen.'}
          </span>
        </button>
        <p className="hint">
          Gemessen wird die Vorschau an der Stelle des Abspielkopfs. Der Export rechnet in voller Auflösung — an den
          Farben ändert das nichts.
        </p>
      </div>
    </Sheet>
  );
}
